"use client";

import { useState, useEffect } from "react";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MultiSelectEnhanced } from "@/components/ui/multi-select-enhanced";
import { toast } from "sonner";
import { useAuth } from "@/contexts/auth-and-permissions-context";
import { STATUS_IDS } from "@/lib/constants/statuses";
import {
  getTodayString,
  getDateValidationError,
} from "@/lib/utils/date-helpers";

interface PriceModifier {
  id: number;
  venue_id: number;
  name: string;
  start_date: string;
  end_date: string;
  modifier_type: "discount" | "surcharge";
  modifier_value: number;
  status_id: number;
  price_points?: number[] | null;
}

interface PriceModifierFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  venueId: number;
  modifier: PriceModifier | null;
  onSuccess: () => void;
}

export function PriceModifierFormDialog({
  open,
  onOpenChange,
  venueId,
  modifier,
  onSuccess,
}: PriceModifierFormDialogProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [pricePoints, setPricePoints] = useState<any[]>([]);
  const [loadingPricePoints, setLoadingPricePoints] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    start_date: "",
    end_date: "",
    modifier_type: "discount" as "discount" | "surcharge",
    modifier_value: "",
    status_id: STATUS_IDS.PRICE_MODIFIER_ACTIVE as number,
    price_points: [] as number[],
  });

  const isSuperAdmin = user?.role_id === 1;
  const isBookingAdmin = user?.role_id === 2;

  // Fetch available price points for the venue
  const fetchPricePoints = async () => {
    try {
      setLoadingPricePoints(true);

      const response = await fetch(`/api/venues/${venueId}/price-points`);
      if (!response.ok) {
        throw new Error("Failed to fetch price points");
      }

      const data = await response.json();
      console.log("Price points API response:", data);

      const pricePointsArray = data.pricePoints || [];
      setPricePoints(pricePointsArray);

      if (pricePointsArray.length === 0) {
        toast.info(
          "No active price points found. Create some shows with price points first.",
        );
      }
    } catch (error) {
      console.error("Error fetching price points:", error);
      toast.error("Failed to load price points");
    } finally {
      setLoadingPricePoints(false);
    }
  };

  // Booking admins can only edit certain fields
  const canEditField = (field: string) => {
    if (isSuperAdmin) return true;
    if (isBookingAdmin && modifier) {
      return [
        "name",
        "start_date",
        "end_date",
        "modifier_value",
        "price_points",
      ].includes(field);
    }
    return true; // Allow all fields for new modifiers
  };

  useEffect(() => {
    if (open) {
      fetchPricePoints();
    }
  }, [open, venueId]);

  useEffect(() => {
    if (modifier) {
      // Convert back to signed value for display (negative for discounts)
      const displayValue =
        modifier.modifier_type === "discount"
          ? -Math.abs(modifier.modifier_value)
          : Math.abs(modifier.modifier_value);

      setFormData({
        name: modifier.name,
        start_date: modifier.start_date,
        end_date: modifier.end_date,
        modifier_type: modifier.modifier_type,
        modifier_value: displayValue.toString(),
        status_id:
          modifier.status_id === STATUS_IDS.PRICE_MODIFIER_SCHEDULED
            ? STATUS_IDS.PRICE_MODIFIER_ACTIVE
            : modifier.status_id,
        price_points: modifier.price_points || [],
      });
    } else {
      setFormData({
        name: "",
        start_date: "",
        end_date: "",
        modifier_type: "discount",
        modifier_value: "",
        status_id: STATUS_IDS.PRICE_MODIFIER_ACTIVE,
        price_points: [],
      });
    }
  }, [modifier]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.name ||
      !formData.start_date ||
      !formData.end_date ||
      !formData.modifier_value
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    const value = parseFloat(formData.modifier_value);
    if (isNaN(value)) {
      toast.error("Please enter a valid number for the value");
      return;
    }

    // Automatically determine type based on value
    const modifier_type = value < 0 ? "discount" : "surcharge";

    // Validate date range and past dates
    const dateError = getDateValidationError(
      formData.start_date,
      formData.end_date,
    );
    if (dateError) {
      toast.error(dateError);
      return;
    }

    try {
      setLoading(true);
      const url = modifier
        ? `/api/venues/${venueId}/price-modifiers/${modifier.id}`
        : `/api/venues/${venueId}/price-modifiers`;

      const method = modifier ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
          modifier_type,
          modifier_value: Math.abs(value), // Store absolute value, type determines discount/surcharge
          status_id: formData.status_id,
          price_points: formData.price_points,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to save price modifier");
      }

      toast.success(
        modifier
          ? "Price modifier updated successfully"
          : "Price modifier created successfully",
      );
      onSuccess();
    } catch (error) {
      console.error("Error saving price modifier:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to save price modifier",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>
            {modifier ? "Edit price modifier" : "Create price modifier"}
          </DialogTitle>
          <DialogDescription>
            Set up price adjustments that will be applied to your pricing
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          {/* Modal Content */}
          <div className="overflow-y-auto flex-1 px-1 py-4">
            <div className="space-y-6">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="Enter modifier name"
                  disabled={!canEditField("name")}
                  className="focus-visible:ring-0 focus-visible:border-gray-300"
                />
              </div>

              {/* Value */}
              <div className="space-y-2">
                <Label htmlFor="modifier_value">Value (%)</Label>
                <Input
                  id="modifier_value"
                  type="number"
                  step="0.1"
                  min="-100"
                  max="100"
                  value={formData.modifier_value}
                  onChange={(e) =>
                    setFormData({ ...formData, modifier_value: e.target.value })
                  }
                  placeholder="0"
                  disabled={!canEditField("modifier_value")}
                  className="focus-visible:ring-0 focus-visible:border-gray-300"
                />
                <p className="text-xs text-muted-foreground">
                  Use negative values for discounts (e.g., -10 for 10% off) and
                  positive values for surcharges
                </p>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Start date</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    min={getTodayString()}
                    onChange={(e) =>
                      setFormData({ ...formData, start_date: e.target.value })
                    }
                    disabled={!canEditField("start_date")}
                    className="focus-visible:ring-0 focus-visible:border-gray-300"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="end_date">End date</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) =>
                      setFormData({ ...formData, end_date: e.target.value })
                    }
                    min={formData.start_date}
                    disabled={!canEditField("end_date")}
                    className="focus-visible:ring-0 focus-visible:border-gray-300"
                  />
                </div>
              </div>

              {/* Status */}
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status_id.toString()}
                  onValueChange={(value) =>
                    setFormData({ ...formData, status_id: parseInt(value) })
                  }
                  disabled={!canEditField("status_id")}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem
                      value={STATUS_IDS.PRICE_MODIFIER_ACTIVE.toString()}
                    >
                      Active
                    </SelectItem>
                    <SelectItem
                      value={STATUS_IDS.PRICE_MODIFIER_INACTIVE.toString()}
                    >
                      Inactive
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Price Points Selection */}
              <div className="space-y-2">
                <Label htmlFor="pricePoints">Apply to price points</Label>
                <MultiSelectEnhanced
                  options={pricePoints.map((pp) => ({
                    label: `${pp.showName} - ${pp.label}`,
                    value: pp.id.toString(),
                  }))}
                  selected={formData.price_points.map(String)}
                  onChange={(selected) =>
                    setFormData({
                      ...formData,
                      price_points: selected.map(Number),
                    })
                  }
                  placeholder={
                    loadingPricePoints
                      ? "Loading price points..."
                      : "Select price points..."
                  }
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  Select specific price points where this modifier applies.
                </p>
              </div>

              {isBookingAdmin && modifier && (
                <p className="text-sm text-muted-foreground">
                  As a booking admin, you can only edit the name, dates, value,
                  and price points fields.
                </p>
              )}
            </div>
          </div>
          <DialogFooter className="flex-shrink-0 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading
                ? "Saving..."
                : modifier
                  ? "Update Modifier"
                  : "Create Modifier"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
